import { ProtocolSettings } from "./ProtocolSettings";
import { CommandLineSettings } from "./CommandLineSettings";
import { ProtocolSettingsTypes } from "./ProtocolSettingsTypes";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { Injectable } from "@angular/core";
import { ObjUtils } from "../Utils";

@Injectable()
export class HttpSettings extends CommandLineSettings {

	public static readonly ObjSer_ObjectName = "HttpSettings_ObjectName";

	private static readonly MERAKI_API_KEY_NAME: string = "X-Cisco-Meraki-API-Key";
	private static readonly MERAKI_API_KEY_NAME_2 = "Meraki-API-Key";

	public UseHTTPS: boolean;
	public TargetServer: string;
	public OrganizationFilter: string;
	public SiteNetworkFilter: string;
	public IsMerakiKey: boolean;
	public IsRuckusKey: boolean;
	public IsMistKey: boolean;
	public IsUniFiKey: boolean;
	public IsExtremeIQKey: boolean;
	public IsArubaInstantOnKey: boolean;

	constructor() {
		super();
		this.Port = this.DefaultPort;
		this.UseHTTPS = true;
		this.TargetServer = "";
		this.OrganizationFilter = "";
		this.SiteNetworkFilter = "";
		this.IsMerakiKey = false;
		this.IsRuckusKey = false;
		this.IsMistKey = false;
		this.IsUniFiKey = false;
		this.IsExtremeIQKey = false;
		this.IsArubaInstantOnKey = false;
	}

	public get HasTargetServer(): boolean {
		return !!this.TargetServer;
	}

	public get HasOrganization(): boolean {
		return !!this.OrganizationFilter;
	}

	public get HasSiteNetworkFilter(): boolean {
		return !!this.SiteNetworkFilter;
	}

	public get SettingsType(): string {
		return ProtocolSettingsTypes.HTTP;
	}

	public get DefaultPort(): number {
		return 443;
	}

	public get IsMerakiApiKey(): boolean {
		if (this.IsMerakiKey)
			return true;

		if (this.HasUsername) {
			let lcUsername: string = this.UserName.toLowerCase();

			if (lcUsername === HttpSettings.MERAKI_API_KEY_NAME.toLowerCase())
				return true;

			if (lcUsername === HttpSettings.MERAKI_API_KEY_NAME_2.toLowerCase())
				return true;
		}

		return false;
	}

	public Copy(obj: ProtocolSettings): void {
		if (!(obj instanceof HttpSettings))
			return;
		let other = (obj as HttpSettings);

		super.Copy(obj);

		this.UseHTTPS = other.UseHTTPS;
		this.TargetServer = other.TargetServer;
		this.OrganizationFilter = other.OrganizationFilter;
		this.SiteNetworkFilter = other.SiteNetworkFilter;
		this.IsMerakiKey = other.IsMerakiKey;
		this.IsUniFiKey = other.IsUniFiKey;
		this.IsRuckusKey = other.IsRuckusKey;
		this.IsMistKey = other.IsMistKey;
		this.IsExtremeIQKey = other.IsExtremeIQKey;
		this.IsArubaInstantOnKey = other.IsArubaInstantOnKey;
	}

	public Clone(): ProtocolSettings {
		let clone = new HttpSettings();
		clone.Copy(this);
		return clone;
	}

	public Equals(obj: any): boolean {
		if (!super.Equals(obj))
			return false;

		if (!(obj instanceof HttpSettings))
			return false;
		let other: HttpSettings = (obj as HttpSettings);

		if (this.UseHTTPS !== other.UseHTTPS)
			return false;

		if (!ObjUtils.Equals(this.TargetServer, other.TargetServer))
			return false;
		if (!ObjUtils.Equals(this.OrganizationFilter, other.OrganizationFilter))
			return false;
		if (!ObjUtils.Equals(this.SiteNetworkFilter, other.SiteNetworkFilter))
			return false;

		return (this.IsMerakiKey === other.IsMerakiKey &&
			this.IsUniFiKey === other.IsUniFiKey &&
			this.IsRuckusKey === other.IsRuckusKey &&
			this.IsMistKey === other.IsMistKey &&
			this.IsExtremeIQKey === other.IsExtremeIQKey &&
			this.IsArubaInstantOnKey === other.IsArubaInstantOnKey);
	}

	public get IsProprietaryApiKey(): boolean {
		if (this.IsMerakiApiKey)
			return true;
		if (this.IsMerakiKey)
			return true;
		if (this.IsRuckusKey)
			return true;
		if (this.IsMistKey)
			return true;
		if (this.IsUniFiKey)
			return true;
		if (this.IsExtremeIQKey)
			return true;
		if (this.IsArubaInstantOnKey)
			return true;

		return false;
	}

	public Write(obj: any, context: any): void {
		let objName = "http";
		if (!!context && !!context[HttpSettings.ObjSer_ObjectName]) {
			objName = context[HttpSettings.ObjSer_ObjectName];
		}

		obj[objName] = {};
		obj = obj[objName];

		super.Write(obj, context);

		obj.use_https = ValueEncoder.EncodeBoolean(this.UseHTTPS);
		obj.target_server = this.TargetServer;
		obj.org_filter = this.OrganizationFilter;
		obj.site_network_filter = this.SiteNetworkFilter;
		obj.is_meraki_key = ValueEncoder.EncodeBoolean(this.IsMerakiKey);
		obj.is_unifi_key = ValueEncoder.EncodeBoolean(this.IsUniFiKey);
		obj.is_ruckus_key = ValueEncoder.EncodeBoolean(this.IsRuckusKey);
		obj.is_mist_key = ValueEncoder.EncodeBoolean(this.IsMistKey);
		obj.is_extreme_iq_key = ValueEncoder.EncodeBoolean(this.IsExtremeIQKey);
		obj.is_aruba_instant_on_key = ValueEncoder.EncodeBoolean(this.IsArubaInstantOnKey);
	}

	public Read(obj: any, context: any): void {
		let objName = "http";
		if (!!context && !!context[HttpSettings.ObjSer_ObjectName]) {
			objName = context[HttpSettings.ObjSer_ObjectName];
		}

		if (!obj[objName])
			throw new Error();
		obj = obj[objName];

		super.Read(obj, context);

		if (obj.use_https !== undefined)
			this.UseHTTPS = ValueEncoder.DecodeBoolean(obj.use_https);

		if (obj.target_server !== undefined)
			this.TargetServer = obj.target_server;

		if (obj.org_filter !== undefined)
			this.OrganizationFilter = obj.org_filter;

		if (obj.site_network_filter !== undefined)
			this.SiteNetworkFilter = obj.site_network_filter;

		if (obj.is_meraki_key !== undefined)
			this.IsMerakiKey = ValueEncoder.DecodeBoolean(obj.is_meraki_key);

		if (obj.is_unifi_key !== undefined)
			this.IsUniFiKey = ValueEncoder.DecodeBoolean(obj.is_unifi_key);

		if (obj.is_ruckus_key !== undefined)
			this.IsRuckusKey = ValueEncoder.DecodeBoolean(obj.is_ruckus_key);

		if (obj.is_mist_key !== undefined)
			this.IsMistKey = ValueEncoder.DecodeBoolean(obj.is_mist_key);

		if (obj.is_extreme_iq_key !== undefined)
			this.IsExtremeIQKey = ValueEncoder.DecodeBoolean(obj.is_extreme_iq_key);

		if (obj.is_aruba_instant_on_key !== undefined)
			this.IsArubaInstantOnKey = ValueEncoder.DecodeBoolean(obj.is_aruba_instant_on_key);
	}

}
